import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Package, Truck, CheckCircle, XCircle, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import OrdersAPI from '../utils/ordersAPI';
import ShipmentsAPI from '../utils/shipmentsAPI';
import PaymentsAPI from '../utils/paymentsAPI';

export default function OrdersPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipmentsByOrderCode, setShipmentsByOrderCode] = useState({});
  const [shipmentLoadingByOrderCode, setShipmentLoadingByOrderCode] = useState({});
  const [paymentLoadingByOrderCode, setPaymentLoadingByOrderCode] = useState({});
  const [paymentCompletedByOrderCode, setPaymentCompletedByOrderCode] = useState({});

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await OrdersAPI.getOrders();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Failed to load orders:', error);
      if (error.message !== 'NOT_AUTHENTICATED') {
        toast.error('Siparişler yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadOrders();
    } else {
      setOrders([]);
      setLoading(false);
    }
  }, [isAuthenticated, loadOrders, user?.id]);

  const setShipmentLoading = (orderCode, value) => {
    setShipmentLoadingByOrderCode((prev) => ({
      ...prev,
      [orderCode]: value,
    }));
  };

  const loadShipment = useCallback(async (orderCode) => {
    try {
      setShipmentLoading(orderCode, true);
      const data = await ShipmentsAPI.getShipment(orderCode);
      setShipmentsByOrderCode((prev) => ({
        ...prev,
        [orderCode]: data,
      }));
    } catch (e) {
      if (e?.message !== 'SHIPMENT_NOT_FOUND' && e?.message !== 'NOT_AUTHENTICATED') {
        toast.error('Kargo bilgisi yüklenemedi');
      }
      if (e?.message === 'SHIPMENT_NOT_FOUND') {
        setShipmentsByOrderCode((prev) => {
          const next = { ...prev };
          delete next[orderCode];
          return next;
        });
      }
    } finally {
      setShipmentLoading(orderCode, false);
    }
  }, []);

  const createShipment = useCallback(async (orderCode) => {
    try {
      setShipmentLoading(orderCode, true);
      const data = await ShipmentsAPI.createShipment({ orderCode });
      setShipmentsByOrderCode((prev) => ({
        ...prev,
        [orderCode]: data,
      }));
      toast.success('Kargo oluşturuldu');
    } catch (e) {
      if (e?.message !== 'NOT_AUTHENTICATED') {
        toast.error('Kargo oluşturulamadı');
      }
    } finally {
      setShipmentLoading(orderCode, false);
    }
  }, []);

  const refreshTracking = useCallback(async (orderCode) => {
    try {
      setShipmentLoading(orderCode, true);
      const shipment = shipmentsByOrderCode?.[orderCode];
      const data = await ShipmentsAPI.refreshTracking({
        orderCode,
        deliveryCompanyName: shipment?.delivery_company_name || shipment?.deliveryCompanyName || null,
        statusHistory: true,
      });
      setShipmentsByOrderCode((prev) => ({
        ...prev,
        [orderCode]: data,
      }));
      toast.success('Takip güncellendi');
    } catch (e) {
      if (e?.message !== 'NOT_AUTHENTICATED') {
        toast.error('Takip güncellenemedi');
      }
    } finally {
      setShipmentLoading(orderCode, false);
    }
  }, [shipmentsByOrderCode]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'shipping':
        return <Truck className="w-6 h-6 text-blue-600" />;
      case 'cancelled':
        return <XCircle className="w-6 h-6 text-red-600" />;
      default:
        return <Package className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipping':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const CourierProgressBar = ({ steps }) => {
    const list = Array.isArray(steps) ? steps : [];
    const filtered = list.filter((s) => {
      const key = String(s?.key || s?.label || 'step');
      const label = String(s?.label || key);
      return label !== 'Başlangıca Dönüyor' && label !== 'Başlangıca Ulaştı';
    });
    if (filtered.length === 0) return null;

    const activeIndex = filtered.findIndex((s) => String(s?.status || 'todo') === 'active');
    const lastDoneIndex = (() => {
      for (let i = filtered.length - 1; i >= 0; i -= 1) {
        if (String(filtered[i]?.status || 'todo') === 'done') return i;
      }
      return -1;
    })();
    const progressIndex = activeIndex >= 0 ? activeIndex : lastDoneIndex;
    const progressPercent = progressIndex <= 0 ? 0 : (progressIndex / (filtered.length - 1)) * 100;

    return (
      <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
        <div className="text-xs font-semibold text-gray-700">Kurye İlerleme</div>
        <div className="mt-3 relative">
          <div className="absolute left-0 right-0 top-2 h-px bg-gray-200" />
          {progressPercent > 0 ? (
            <div
              className="absolute left-0 top-2 h-px bg-trendyol-orange"
              style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
            />
          ) : null}
          <div className="flex items-start justify-between gap-2">
            {filtered.map((s) => {
              const key = String(s?.key || s?.label || 'step');
              const status = String(s?.status || 'todo');
              const label = String(s?.label || key);
              const color =
                status === 'done'
                  ? 'bg-green-600'
                  : status === 'active'
                    ? 'bg-trendyol-orange'
                    : 'bg-gray-200';
              const textColor = status === 'todo' ? 'text-gray-500' : 'text-gray-800';
              return (
                <div key={key} className="flex flex-col items-center flex-1 min-w-0">
                  <div className={`h-3 w-3 rounded-full ${color} z-10`} />
                  <div className={`mt-2 text-[11px] leading-tight text-center ${textColor}`}>{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="bg-white rounded-xl p-12 text-center max-w-md">
          <Package className="w-24 h-24 mx-auto text-gray-300 mb-6" />
          <h2 className="font-manrope font-bold text-2xl mb-4">Giriş Yapın</h2>
          <p className="text-gray-600 mb-8">Siparişlerinizi görüntülemek için giriş yapmanız gerekiyor.</p>
          <Link
            to="/giris"
            className="inline-block bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12" data-testid="orders-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-manrope font-bold text-2xl md:text-3xl">Siparişlerim</h1>
          <Link
            to="/hesabim"
            className="text-trendyol-orange hover:underline font-medium"
          >
            Hesabıma Dön
          </Link>
        </div>

        <div className="bg-white rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Sipariş numarası veya ürün ara"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none"
                data-testid="search-orders-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none"
              data-testid="filter-orders-select"
            >
              <option value="all">Tüm Siparişler</option>
              <option value="delivered">Teslim Edildi</option>
              <option value="shipping">Kargoda</option>
              <option value="cancelled">İptal Edildi</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Package className="w-24 h-24 mx-auto text-gray-300 mb-6" />
            <h2 className="font-manrope font-bold text-2xl mb-4">Henüz Siparişiniz Yok</h2>
            <p className="text-gray-600 mb-8">Alışverişe başlayın ve ilk siparişinizi verin!</p>
            <Link
              to="/"
              className="inline-block bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Alışverişe Başla
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.order_id} className="bg-white rounded-xl p-6" data-testid={`order-${order.order_id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-4 mb-4 md:mb-0">
                    {getStatusIcon(order.status)}
                    <div>
                      <p className="font-semibold text-lg">Sipariş #{order.order_id}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-4 py-2 rounded-full font-medium text-sm ${getStatusColor(order.status)}`}>
                      {order.statusText}
                    </span>
                    <button
                      type="button"
                      className="text-trendyol-orange hover:underline font-medium flex items-center gap-1"
                    >
                      Detay
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <CourierProgressBar steps={order.progress_steps} />

                <div className="space-y-3 mb-4">
                  {(order.items || []).map((item, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Link to={`/urun/${item.product_id}`} className="flex-shrink-0">
                          <img
                            src={item.product_image || 'https://via.placeholder.com/80?text=Ürün'}
                            alt={item.product_name}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity cursor-pointer"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/80?text=Ürün';
                            }}
                          />
                        </Link>
                        <div className="flex-1">
                          <Link
                            to={`/urun/${item.product_id}`}
                            className="font-medium text-gray-900 hover:text-trendyol-orange transition-colors"
                          >
                            {item.product_name}
                          </Link>
                          <p className="text-sm text-gray-600">Adet: {item.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {(Number(item.product_price) * (Number(item.quantity) || 0)).toFixed(2)} TL
                          </p>
                          <p className="text-xs text-gray-500">{Number(item.product_price).toFixed(2)} TL / adet</p>
                        </div>
                      </div>

                      {Array.isArray(item.extras) && item.extras.length > 0 && (
                        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                          <div className="text-xs font-semibold text-gray-700">Ekstralar</div>
                          <div className="mt-2 space-y-1">
                            {item.extras.map((ex) => (
                              <div key={String(ex.extra_id)} className="flex items-center justify-between gap-3">
                                {ex.ui_id ? (
                                  <Link
                                    to={`/urun/${ex.ui_id}`}
                                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                    className="flex items-center gap-2 min-w-0"
                                  >
                                    <div className="h-8 w-8 rounded-md bg-gray-200 overflow-hidden flex-shrink-0">
                                      {ex.extra_image ? (
                                        <img src={ex.extra_image} alt={ex.extra_name || ''} className="h-full w-full object-cover" />
                                      ) : null}
                                    </div>
                                    <div className="text-xs text-gray-700 truncate">{ex.extra_name || 'Ekstra'}</div>
                                  </Link>
                                ) : (
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="h-8 w-8 rounded-md bg-gray-200 overflow-hidden flex-shrink-0">
                                      {ex.extra_image ? (
                                        <img src={ex.extra_image} alt={ex.extra_name || ''} className="h-full w-full object-cover" />
                                      ) : null}
                                    </div>
                                    <div className="text-xs text-gray-700 truncate">{ex.extra_name || 'Ekstra'}</div>
                                  </div>
                                )}
                                <div className="text-right">
                                  <div className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                                    {Number(ex.quantity) || 1} adet · {((Number(ex.extra_price) || 0) * (Number(ex.quantity) || 1)).toFixed(2)} TL
                                  </div>
                                  <div className="text-[11px] text-gray-500 whitespace-nowrap">
                                    {(Number(ex.extra_price) || 0).toFixed(2)} TL / adet
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Ara Toplam</span>
                    <span className="font-medium">{Number(order.subtotal || 0).toFixed(2)} TL</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Kargo</span>
                    <span className="font-medium">
                      {Number(order.shipping_cost || 0) === 0 ? (
                        <span className="text-green-600">Ücretsiz</span>
                      ) : (
                        `${Number(order.shipping_cost || 0).toFixed(2)} TL`
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Ödeme Yöntemi</span>
                    <span className="font-medium">
                      {order.payment_method === 'credit-card' && '💳 Kredi Kartı'}
                      {order.payment_method === 'transfer' && '🏦 Havale/EFT'}
                      {order.payment_method === 'door' && '🚪 Kapıda Ödeme'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="font-medium text-gray-900">Toplam Tutar</span>
                    <span className="font-manrope font-bold text-xl text-trendyol-orange">
                      {Number(order.total || 0).toFixed(2)} TL
                    </span>
                  </div>
                </div>

                {/* <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-gray-900">Kargo Takibi</p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="text-sm px-3 py-2 rounded-lg border border-gray-200 hover:border-trendyol-orange hover:text-trendyol-orange transition-colors"
                        onClick={() => loadShipment(order.order_id)}
                        disabled={!!shipmentLoadingByOrderCode[order.order_id]}
                      >
                        Yenile
                      </button>
                      <button
                        type="button"
                        className="text-sm px-3 py-2 rounded-lg bg-trendyol-orange text-white hover:bg-orange-600 transition-colors"
                        onClick={() => createShipment(order.order_id)}
                        disabled={!!shipmentLoadingByOrderCode[order.order_id]}
                      >
                        Kargo Oluştur
                      </button>
                    </div>
                  </div>

                  {shipmentLoadingByOrderCode[order.order_id] ? (
                    <div className="text-sm text-gray-600">Yükleniyor...</div>
                  ) : shipmentsByOrderCode[order.order_id] ? (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Durum:</span>{' '}
                          <span className="font-medium text-gray-900">{shipmentsByOrderCode[order.order_id].status || 'created'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Takip No:</span>{' '}
                          <span className="font-medium text-gray-900">{shipmentsByOrderCode[order.order_id].tracking_number || '-'}</span>
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2">
                          <button
                            type="button"
                            className="text-sm px-3 py-2 rounded-lg border border-gray-200 hover:border-trendyol-orange hover:text-trendyol-orange transition-colors"
                            onClick={() => refreshTracking(order.order_id)}
                            disabled={!!shipmentLoadingByOrderCode[order.order_id] || !shipmentsByOrderCode[order.order_id].tracking_number}
                          >
                            Takibi Güncelle
                          </button>
                          {shipmentsByOrderCode[order.order_id].label_url ? (
                            <a
                              href={shipmentsByOrderCode[order.order_id].label_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm px-3 py-2 rounded-lg border border-gray-200 hover:border-trendyol-orange hover:text-trendyol-orange transition-colors"
                            >
                              Etiket
                            </a>
                          ) : null}
                        </div>
                      </div>

                      {shipmentsByOrderCode[order.order_id].raw?.track ? (
                        <div className="mt-3 text-xs text-gray-600 whitespace-pre-wrap">
                          {JSON.stringify(shipmentsByOrderCode[order.order_id].raw.track, null, 2)}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">Bu sipariş için henüz kargo kaydı yok.</div>
                  )}
                </div> */}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}